import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { CopyButton, StatusPill } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
  verification: "Ownership (TXT)",
  spf: "SPF (TXT)",
  dkim: "DKIM (TXT)",
  dmarc: "DMARC (TXT)",
  mx: "MX (inbound)",
};
const ORDER = ["verification", "spf", "dkim", "dmarc", "mx"];

export default function DomainDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [domain, setDomain] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const load = () => api.get(`/domains/${id}`).then((r) => setDomain(r.data)).catch(() => navigate("/app/domains"));
  useEffect(() => { load(); }, [id]);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/domains/${id}/verify`);
      if (data.checking) toast.info("A DNS check is already running");
      else if (data.verified) toast.success("Domain DNS verified");
      else toast.error("Records not found yet. DNS can take time to propagate.");
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setVerifying(false); }
  };

  if (!domain) return <div className="text-slate-500">Loading…</div>;
  const records = domain.dns_records || {};

  return (
    <div className="space-y-6 fade-up">
      <button data-testid="domain-back-button" onClick={() => navigate("/app/domains")} className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to domains</button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 data-testid="domain-detail-name" className="display mono break-all text-2xl font-bold">{domain.domain}</h1>
            <StatusPill status={domain.status} testid="detail-status" />
          </div>
          <p className="mt-1 text-sm text-slate-400">Ownership, SPF, DKIM and DMARC are required for sending.</p>
        </div>
        <button onClick={verify} disabled={verifying} data-testid="domain-verify-button"
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-400 disabled:opacity-60">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {verifying ? "Checking…" : "Check now"}
        </button>
      </div>

      <section data-testid="domain-auto-check-panel" className="border-y border-slate-800 py-4 text-sm">
        <div data-testid="domain-auto-check-cadence" className="text-cyan-300">{domain.status === "pending" ? "Automatic checks every 15 minutes" : "DNS verification complete"}</div>
        <div className="mt-3 grid gap-3 text-xs text-slate-400 sm:grid-cols-2">
          <p data-testid="domain-last-check">Last checked: {domain.last_checked_at ? new Date(domain.last_checked_at).toLocaleString() : "Not checked yet"}</p>
          <p data-testid="domain-next-check">Next check: {domain.status === "pending" ? new Date(domain.next_check_at).toLocaleString() : "No pending check"}</p>
        </div>
        {!domain.is_notification_sender && <p data-testid="domain-notification-status" className="mt-3 break-words text-xs text-slate-400">Owner notice → {user?.email} · {domain.notification?.status === "sent" ? "Accepted by recipient server" : domain.notification?.status || "Waiting for verification"}{domain.notification?.last_error && <span className="mt-1 block text-amber-300">{domain.notification.last_error}</span>}</p>}
      </section>

      <div className="space-y-3">
        {ORDER.map((key) => {
          const rec = records[key];
          if (!rec) return null;
          const ok = domain.verification?.[key];
          return (
            <div key={key} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="display text-sm font-semibold">{LABELS[key]}</span>
                  <span className="mono rounded border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">{rec.type}</span>
                </div>
                <span data-testid={`dns-status-${key}`} className={`mono text-xs ${ok ? "text-emerald-400" : "text-amber-400"}`}>{ok ? "✓ found" : key === "mx" ? "optional" : "• pending"}</span>
              </div>
              {domain.dns_errors?.[key] && <p data-testid={`dns-error-${key}`} className="mb-3 text-xs text-amber-300">{domain.dns_errors[key]}</p>}
              {key === "mx" && <p data-testid="dns-mx-note" className="mb-3 text-xs text-slate-400">Optional for RelayMX inbound mail. Keep your existing MX if another service handles your inbox.</p>}
              {key === "spf" && <p data-testid="dns-spf-note" className="mb-3 text-xs text-slate-400">Merge this sending IP into your existing SPF record; never publish two SPF records.</p>}
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div className="min-w-0">
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Host / Name</div>
                  <div className="flex items-center gap-2">
                    <code data-testid={`dns-host-${key}`} className="mono min-w-0 flex-1 break-all rounded-lg border border-slate-800 bg-slate-950 p-2.5 text-xs text-slate-300">{rec.host}</code>
                    <CopyButton value={rec.host} label="Host" testid={`dns-copy-host-${key}`} />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">Value</div>
                  <div className="flex items-center gap-2">
                    <code data-testid={`dns-value-${key}`} className="mono min-w-0 flex-1 break-all rounded-lg border border-slate-800 bg-slate-950 p-2.5 text-xs text-emerald-400">{rec.value || "Waiting for the administrator to configure the public sending IP."}</code>
                    {rec.value && <CopyButton value={rec.value} label="Value" testid={`dns-copy-${key}`} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {domain.status === "verified" && (
        <div data-testid="domain-verified-confirmation" className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300">
          <ShieldCheck className="h-5 w-5 shrink-0" /> DNS verified. Live sends also require the sending server's public IP and DNS setup.
        </div>
      )}
    </div>
  );
}
